import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Copy, PauseCircle, PlayCircle, ShieldCheck, Users } from 'lucide-react';
import { toast } from 'react-toastify';
import { api } from '../lib_api';

type State = {
  provider: null | {
    email: string;
    name?: string | null;
    sharingEnabled: boolean;
    minimumConfidenceExclusive: number;
  };
  followers: Array<{
    id: number;
    email: string;
    fullName?: string | null;
    status: string;
    lastError?: string | null;
    planCode?: string | null;
    dailyLimit: number;
    copiesToday: number;
    tradingAccount: { label?: string | null; login?: string | null; server?: string | null; accountType?: string | null };
  }>;
};

function humanize(value?: string | null) {
  return value ? value.replaceAll('_', ' ') : 'Ready';
}

export default function CopyTrading() {
  const queryClient = useQueryClient();
  const state = useQuery({
    queryKey: ['admin', 'copy-trading'],
    queryFn: async () => (await api.get<State>('/api/admin/copy-trading')).data,
    refetchInterval: 20000,
  });
  const toggle = useMutation({
    mutationFn: async (sharingEnabled: boolean) => (
      await api.patch<State>('/api/admin/copy-trading/provider', { sharingEnabled })
    ).data,
    onSuccess: (data) => {
      queryClient.setQueryData(['admin', 'copy-trading'], data);
      toast.success(data.provider?.sharingEnabled ? 'Copy Trading sharing enabled.' : 'Copy Trading sharing paused.');
    },
    onError: (error: any) => toast.error(error?.response?.data?.message || error?.message || 'Update failed.'),
  });
  const provider = state.data?.provider;
  const followers = state.data?.followers || [];

  return (
    <div className="space-y-4">
      <section className="rounded-lg border border-emerald-300/50 bg-white p-5 shadow-sm dark:border-emerald-700/60 dark:bg-[#111f2b]">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="rounded-md bg-emerald-100 p-2 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300"><Copy className="h-5 w-5" /></div>
            <div>
              <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Approved provider</p>
              <h2 className="text-lg font-semibold text-gray-950 dark:text-white">{provider?.name || provider?.email || 'Provider not configured'}</h2>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">Only live entries above {provider?.minimumConfidenceExclusive ?? 92}% confidence are distributed.</p>
            </div>
          </div>
          <button
            type="button"
            disabled={!provider || toggle.isPending}
            onClick={() => toggle.mutate(!provider?.sharingEnabled)}
            className={`inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-semibold ${provider?.sharingEnabled ? 'bg-rose-100 text-rose-800 dark:bg-rose-950/50 dark:text-rose-200' : 'bg-emerald-600 text-white'}`}
          >
            {provider?.sharingEnabled ? <PauseCircle className="h-4 w-4" /> : <PlayCircle className="h-4 w-4" />}
            {toggle.isPending ? 'Updating...' : provider?.sharingEnabled ? 'Pause sharing' : 'Start sharing'}
          </button>
        </div>
        <div className="mt-4 flex flex-wrap gap-2 text-xs">
          <span className="rounded-full bg-gray-100 px-3 py-1 text-gray-700 dark:bg-white/10 dark:text-gray-200"><Users className="mr-1 inline h-3.5 w-3.5" /> {followers.length} followers</span>
          <span className="rounded-full bg-gray-100 px-3 py-1 text-gray-700 dark:bg-white/10 dark:text-gray-200"><ShieldCheck className="mr-1 inline h-3.5 w-3.5" /> Live USD accounts only</span>
        </div>
      </section>

      <section className="overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-darkCard dark:bg-[#111f2b]">
        <div className="border-b border-gray-200 p-4 dark:border-darkCard">
          <h3 className="font-semibold text-gray-950 dark:text-white">Copy Trading followers</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">Gold receives up to 2 copies daily; Platinum receives up to 5.</p>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-left text-sm">
            <thead className="bg-gray-50 text-xs uppercase text-gray-500 dark:bg-white/5 dark:text-gray-400">
              <tr><th className="p-3">User</th><th className="p-3">Plan</th><th className="p-3">MT5 account</th><th className="p-3">Status</th><th className="p-3">Today</th><th className="p-3">Readiness</th></tr>
            </thead>
            <tbody>
              {followers.map((follower) => (
                <tr key={follower.id} className="border-t border-gray-100 dark:border-white/10">
                  <td className="p-3"><div className="font-medium text-gray-950 dark:text-white">{follower.fullName || follower.email}</div><div className="text-xs text-gray-500">{follower.email}</div></td>
                  <td className="p-3 uppercase">{String(follower.planCode || 'none').replace('pickaxe-', '')}</td>
                  <td className="p-3"><div>{follower.tradingAccount.label || follower.tradingAccount.login}</div><div className="text-xs text-gray-500">{follower.tradingAccount.server} · {follower.tradingAccount.accountType}</div></td>
                  <td className="p-3"><span className={`rounded-full px-2 py-1 text-xs ${follower.status === 'active' ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-100 text-gray-700'}`}>{follower.status}</span></td>
                  <td className="p-3">{follower.copiesToday} / {follower.dailyLimit}</td>
                  <td className="p-3 text-xs text-gray-500 dark:text-gray-400">{humanize(follower.lastError)}</td>
                </tr>
              ))}
              {!state.isLoading && followers.length === 0 ? <tr><td className="p-6 text-center text-gray-500" colSpan={6}>No users have enabled Copy Trading yet.</td></tr> : null}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
